import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../store';
import { fetchDeletedClusters, restoreCluster, permanentlyDeleteCluster } from '../store/slices/clusterSlice';
import { addNotification, showModal } from '../store/slices/uiSlice';
import { FiArrowLeft, FiRefreshCw, FiTrash2, FiDatabase } from 'react-icons/fi';

const ClustersDeletedPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { deletedClusters, loadingCluster, error } = useSelector((state: RootState) => state.clusters);

    useEffect(() => {
        dispatch(fetchDeletedClusters());
    }, [dispatch]);

    const handleRestore = (id: string, clusterId: string) => {
        dispatch(
            showModal({
                title: 'Confirm Restoration',
                content: `Are you sure you want to restore cluster "${clusterId}"?`,
                confirmLabel: 'Restore',
                cancelLabel: 'Cancel',
                onConfirm: async () => {
                    try {
                        await dispatch(restoreCluster(id)).unwrap();

                        dispatch(
                            addNotification({
                                type: 'success',
                                message: `Cluster "${clusterId}" was successfully restored.`,
                            })
                        );
                    } catch (error: any) {
                        dispatch(
                            addNotification({
                                type: 'error',
                                title: 'Restoration Failed',
                                message: error.message || 'Failed to restore cluster.',
                            })
                        );
                    }
                },
            })
        );
    };

    const handlePermanentDelete = (id: string, clusterId: string) => {
        dispatch(
            showModal({
                title: 'Confirm Permanent Deletion',
                content: `Are you sure you want to permanently delete cluster "${clusterId}"? This action cannot be undone.`,
                confirmLabel: 'Delete Permanently',
                cancelLabel: 'Cancel',
                onConfirm: async () => {
                    try {
                        await dispatch(permanentlyDeleteCluster(id)).unwrap();

                        dispatch(
                            addNotification({
                                type: 'success',
                                message: `Cluster "${clusterId}" was permanently deleted.`,
                            })
                        );
                    } catch (error: any) {
                        dispatch(
                            addNotification({
                                type: 'error',
                                title: 'Deletion Failed',
                                message: error.message || 'Failed to delete cluster permanently.',
                            })
                        );
                    }
                },
            })
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center">
                <button
                    onClick={() => navigate('/clusters')}
                    className="mr-4 inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                    <FiArrowLeft className="mr-2" />
                    Back to Active Clusters
                </button>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Deleted Clusters</h1>
            </div>

            {error && (
                <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-md p-4 text-sm text-red-600 dark:text-red-300">
                    {error}
                </div>
            )}

            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden rounded-lg">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Cluster ID
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Servers
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Username
                                </th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {loadingCluster ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-4 text-center">
                                        <div className="flex justify-center">
                                            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-500"></div>
                                        </div>
                                    </td>
                                </tr>
                            ) : deletedClusters.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                        No deleted clusters found.
                                    </td>
                                </tr>
                            ) : (
                                deletedClusters.map((cluster) => (
                                    <tr key={cluster._id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <FiDatabase className="flex-shrink-0 mr-2 h-5 w-5 text-gray-400" />
                                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {cluster.clusterId}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                            {cluster.Servers.map((server, index) => (
                                                <div key={index}>
                                                    {server.Host}:{server.Port}
                                                </div>
                                            ))}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                            {cluster.UserName}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex justify-end space-x-3">
                                                <button
                                                    onClick={() => handleRestore(cluster._id, cluster.clusterId)}
                                                    className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                                                    title="Restore cluster"
                                                >
                                                    <FiRefreshCw className="h-5 w-5" />
                                                </button>
                                                <button
                                                    onClick={() => handlePermanentDelete(cluster._id, cluster.clusterId)}
                                                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                                    title="Permanently delete cluster"
                                                >
                                                    <FiTrash2 className="h-5 w-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ClustersDeletedPage;